import { useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'
import type { PlanOption } from '../../client'
import customCurve from '../../assets/custom.svg'
import { formatCredits } from '../../lib/format'
import { type Billing, type PlanCopy, creditsFor } from '../../lib/plans'

const PRIMARY =
  'bg-linear-to-b from-stone-700 to-stone-950 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity enabled:hover:opacity-90'
const OUTLINE = 'border border-stone-300 bg-stone-100 text-stone-900 transition-colors enabled:hover:bg-stone-200'

type Props = {
  plan: PlanCopy
  options: PlanOption[]
  billing: Billing
  current: boolean
  subscribed: boolean
  busy: boolean
  onSelect: (option: PlanOption | null) => void
}

export default function PlanCard({
  plan,
  options,
  billing,
  current,
  subscribed,
  busy,
  onSelect,
}: Props) {
  const [stop, setStop] = useState(0)
  const custom = plan.tier === null
  const stops = options.length

  const selected = options[Math.min(stop, stops - 1)] ?? null
  const fill = stops > 1 ? `${(stop / (stops - 1)) * 100}%` : '0%'

  return (
    <article className="flex flex-col rounded-3xl bg-stone-200 p-1 pb-3">
      <div className="flex flex-col rounded-[1.25rem] bg-stone-100 px-5 pt-7 pb-4 md:min-h-[22rem]">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-xl font-semibold text-stone-900">{plan.name}</h3>
          {plan.popular && (
            <span className="rounded-md bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800">
              Most popular
            </span>
          )}
        </div>

        <hr className="my-5 border-stone-200" />

        <div className="flex items-baseline gap-2 whitespace-nowrap">
          <span className="font-heading text-4xl leading-none font-semibold text-stone-900">
            {custom || !selected ? 'Custom' : `$${(selected.amount / 100).toLocaleString()}`}
          </span>
          {!custom && selected && (
            <span className="text-xs font-medium tracking-[0.06em] text-stone-400 uppercase">
              / {billing === 'yearly' ? 'year' : 'month'}
            </span>
          )}
        </div>

        <p className="mt-4 text-sm text-stone-500">{plan.desc}</p>

        {custom || !selected ? (
          <div className="-mx-5 flex flex-1 items-center pt-4 max-md:hidden">
            <img src={customCurve} alt="" aria-hidden className="block w-full" />
          </div>
        ) : (
          <>
            <hr className="mt-5 mb-4 border-stone-200" />
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span className="text-xs font-medium tracking-[0.08em] text-stone-400 uppercase">
                {stops > 1 ? 'Add credits' : 'Credits'}
              </span>
              <span className="text-sm font-medium text-purple-700">
                {formatCredits(creditsFor(selected.credits, billing))} credits
              </span>
            </div>
            {stops > 1 && (
              <>
                <input
                  type="range"
                  min={0}
                  max={stops - 1}
                  step={1}
                  value={stop}
                  onChange={(event) => setStop(Number(event.target.value))}
                  aria-label={`${plan.name} credits`}
                  className="credit-slider mt-0.5"
                  style={{ '--fill': fill } as React.CSSProperties}
                />
                <div className="flex justify-between px-2">
                  {options.map((option, index) => (
                    <span
                      key={option.price_id}
                      className={`size-1 rounded-full transition-colors ${
                        index <= stop ? 'bg-purple-700' : 'bg-stone-300'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="flex flex-1 flex-col px-5 pt-7 pb-2">
        <span className="mb-5 text-xs font-medium tracking-[0.08em] text-stone-400 uppercase">Key features</span>

        <ul className="mb-8 flex flex-col gap-3">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5 text-sm text-stone-900">
              <CheckIcon className="mt-0.5 size-4 shrink-0 text-stone-500" strokeWidth={2.2} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          disabled={busy || current}
          onClick={() => onSelect(selected)}
          className={`mt-auto inline-flex h-10.5 items-center justify-center rounded-xl px-4 text-sm font-medium disabled:opacity-60 ${
            plan.popular ? PRIMARY : OUTLINE
          }`}
        >
          {current ? 'Current plan' : subscribed && plan.tier ? 'Change plan' : plan.cta}
        </button>
      </div>
    </article>
  )
}
